import { ReactNode, useEffect, useState } from "react";
import {
  ColorInput,
  ComboBoxOptionType,
  DiscreteInput,
  DiscreteOption,
  DraggableInput,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { ImageIcon } from "lucide-react";
import {
  PropertyType,
  useTailwindProperties,
} from "@/hooks/useTailwindProperties";
import { safeParseCssStringAsInlineStyles } from "../../utils";
import useGradient, { GradientState } from "../../hooks/useGradient";
import { TempoElement } from "@/utils/tempoElement";
import {
  ArrowBottomLeftIcon,
  ArrowBottomRightIcon,
  ArrowTopLeftIcon,
  ArrowTopRightIcon,
  MixerHorizontalIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from "@radix-ui/react-icons";
import React from "react";
import CustomColorPicker from "../input/CustomColorPicker";
import CustomImageUpload from "../input/CustomImageUpload";
import CustomGradientInput from "../input/CustomGradientInput";

interface Props {
  classList: string[];
  selectedElement: TempoElement;

  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  backgroundColorSet: boolean;
  setBackgroundColorSet: (set: boolean) => void;
  addTailwindClass: (option: ComboBoxOptionType) => void;
  addTailwindClassInstantOnly: (option: ComboBoxOptionType) => void;
  removeTailwindClass: (className: string) => void;
  removeStyles: (property: string) => void;
  cssValues: { [key: string]: any };
  backgroundImage: string;
  setBackgroundImage: (backgroundImage: string) => void;
  backgroundImageSet: boolean;
  setBackgroundImageSet: (set: boolean) => void;
}

enum ViewType {
  Fill = "fill",
  Gradient = "gradient",
  Image = "image",
}

const viewOptions: DiscreteOption[] = [
  {
    key: ViewType.Fill,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Solid</div>
    ),
  },
  {
    key: ViewType.Gradient,
    // @ts-expect-error TS2322
    option: null,
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">
        Gradient
      </div>
    ),
  },
  {
    key: ViewType.Image,
    // @ts-expect-error TS2322
    option: null,
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Image</div>
    ),
  },
];

enum BackgroundImageType {
  None = "none",
  Gradient = "gradient",
  Image = "image",
}

const FillModal: React.FC<Props> = ({
  classList,
  selectedElement,
  backgroundColor,
  setBackgroundColor,
  backgroundColorSet,
  setBackgroundColorSet,
  addTailwindClass,
  addTailwindClassInstantOnly,
  removeStyles,
  cssValues,
  backgroundImage,
  setBackgroundImage,
  backgroundImageSet,
  setBackgroundImageSet,
  removeTailwindClass,
}) => {
  const [view, setView] = useState<ViewType>(() => {
    const backgroundImageValue = cssValues["background-image"];
    if (
      backgroundImageValue?.evaluatedValue?.includes("gradient") ||
      backgroundImageValue?.value?.includes("gradient")
    ) {
      return ViewType.Gradient;
    } else if (
      backgroundImageValue?.evaluatedValue?.includes("url") ||
      backgroundImageValue?.value?.includes("url")
    ) {
      return ViewType.Image;
    } else {
      return ViewType.Fill;
    }
  });

  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR
  );
  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);
  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const fromPositionProperties = useTailwindProperties(
    PropertyType.FROM_POSITION
  );
  const toPositionProperties = useTailwindProperties(PropertyType.TO_POSITION);
  const viaPositionProperties = useTailwindProperties(
    PropertyType.VIA_POSITION
  );

  const buildBackgroundColorPreview = (
    option: ComboBoxOptionType
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return <div className={`w-full h-full`} style={styles}></div>;
  };

  const computedGradient = useGradient(
    cssValues["background-image"]?.evaluatedValue,
    classList,
    selectedElement
  );

  const backgroundImageProperties = useTailwindProperties(
    PropertyType.BACKGROUND_IMAGE
  );

  useEffect(() => {
    if (classList.length === 0) return;

    switch (view) {
      case ViewType.Gradient:
        // remove fill classes
        const fillCls1 = classList.find((c) => c.startsWith("bg-"));

        if (fillCls1) {
          removeTailwindClass(fillCls1);
        }

        // remove image classes
        const imgCls1 = classList.find((c) => c.startsWith("bg-[url('"));
        if (imgCls1) {
          removeTailwindClass(imgCls1);
        }

        if (!classList.some((c) => c.startsWith("bg-gradient-"))) {
          addTailwindClass(
            // @ts-expect-error TS2345
            backgroundImageProperties.find(
              (p) => p.tailwind === "bg-gradient-to-r"
            )
          );
        }
        break;
      case ViewType.Image:
        // remove fill classes
        const fillCls2 = classList.find((c) => c.startsWith("bg-"));

        if (fillCls2) {
          removeTailwindClass(fillCls2);
        }

        // remove gradient classes
        const gradientCls1 = classList.find((c) =>
          c.startsWith("bg-gradient-")
        );
        if (gradientCls1) {
          removeTailwindClass(gradientCls1);
        }

        if (!classList.some((c) => c.startsWith("bg-[url('"))) {
          addTailwindClass({
            css: "background-image: url('https://storage.googleapis.com/tempo-public-assets/checker.webp')",
            tailwind:
              "bg-[url('https://storage.googleapis.com/tempo-public-assets/checker.webp')]",
          });
        }
        break;
      case ViewType.Fill:
      default:
        const gradientCls2 = classList.find((c) =>
          c.startsWith("bg-gradient-")
        );
        if (gradientCls2) {
          removeTailwindClass(gradientCls2);
        }
        const imgCls2 = classList.find((c) => c.startsWith("bg-[url('"));
        if (imgCls2) {
          removeTailwindClass(imgCls2);
        }

        if (!classList.some((c) => c.startsWith("bg-"))) {
          addTailwindClass(
            // @ts-expect-error TS2345
            backgroundColorProperties.find((p) => p.tailwind === "bg-white")
          );
        }
        break;
    }
  }, [view]);

  const [gradientDirectionIcon, setGradientDirectionIcon] = useState(
    <ArrowRightIcon />
  );

  const [gradient, setGradient] = useState<GradientState>({
    // @ts-expect-error TS2322
    direction: null,
    from: {
      color: {
        value: "#FFFFFF",
        classname: undefined,
      },
      set: false,
    },
  });

  useEffect(() => {
    if (computedGradient) {
      setGradient(computedGradient);

      switch (computedGradient.direction) {
        case "to top":
          setGradientDirectionIcon(<ArrowUpIcon />);
          break;
        case "to right":
          setGradientDirectionIcon(<ArrowRightIcon />);
          break;
        case "to bottom":
          setGradientDirectionIcon(<ArrowDownIcon />);
          break;
        case "to left":
          setGradientDirectionIcon(<ArrowLeftIcon />);
          break;
        case "to top right":
        case "to right top":
          setGradientDirectionIcon(<ArrowTopRightIcon />);
          break;
        case "to top left":
        case "to left top":
          setGradientDirectionIcon(<ArrowTopLeftIcon />);
          break;
        case "to bottom right":
        case "to right bottom":
          setGradientDirectionIcon(<ArrowBottomRightIcon />);
          break;
        case "to bottom left":
        case "to left bottom":
          setGradientDirectionIcon(<ArrowBottomLeftIcon />);
          break;
        default:
          setGradientDirectionIcon(<ArrowRightIcon />);
      }
    } else {
      setGradient({
        // @ts-expect-error TS2322
        direction: null,
        from: {
          color: {
            value: "#FFFFFF",
            classname: undefined,
          },
          set: false,
        },
      });
    }
  }, [computedGradient]);

  return (
    <div className="grid grid-cols-14 items-center gap-x-3 gap-y-2">
      <div className="col-span-14">
        <DiscreteInput
          className="p-0.5"
          options={viewOptions}
          onOptionSelected={(option) => {
            setView(option.key as ViewType);
          }}
          selected={view}
        />
      </div>
      {view === ViewType.Fill && (
        <>
          <div className="col-span-14">
            <CustomColorPicker
              value={backgroundColor}
              evaluatedValue={cssValues["background-color"].evaluatedValue}
              setValue={setBackgroundColor}
              buildArbitraryClass={(input: string) => {
                return {
                  tailwind: `bg-[${input}]`,
                  css: `background-color: ${input}`,
                };
              }}
              set={backgroundColorSet}
              setSet={setBackgroundColorSet}
              onValueUpdate={(option) => addTailwindClassInstantOnly(option)}
              comboboxOptions={backgroundColorProperties}
              onComboboxSelected={(option) => addTailwindClass(option)}
              buildOptionPreview={buildBackgroundColorPreview}
              tooltipContent="Background color"
              onClear={() => {
                removeStyles("background-color");
              }}
            />
          </div>
        </>
      )}
      {view === ViewType.Gradient && (
        <>
          <CustomGradientInput
            backgroundImage={backgroundImage}
            setBackgroundImage={setBackgroundImage}
            backgroundImageSet={backgroundImageSet}
            setBackgroundImageSet={setBackgroundImageSet}
            backgroundImageProperties={backgroundImageProperties}
            gradientDirectionIcon={gradientDirectionIcon}
            addTailwindClass={addTailwindClass}
            removeTailwindClass={removeTailwindClass}
            classList={classList}
            evaluatedValue={gradient?.from?.color?.value || "#fffff00"}
            // @ts-expect-error TS2322
            set={gradient?.from?.set}
            gradient={gradient}
            setGradient={setGradient}
            setSet={() => {}}
            onValueUpdate={(option) => addTailwindClassInstantOnly(option)}
            fromColorProperties={fromColorProperties}
            toColorProperties={toColorProperties}
            viaColorProperties={viaColorProperties}
            toPositionProperties={toPositionProperties}
            viaPositionProperties={viaPositionProperties}
            fromPositionProperties={fromPositionProperties}
            backgroundColorProperties={backgroundColorProperties}
            buildArbitraryClass={(
              currentColor: "from" | "to" | "via",
              input: string
            ) => {
              switch (currentColor) {
                case "from":
                  return {
                    tailwind: `from-[${input}]`,
                    css: `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`,
                    // `--tw-gradient-from-position: ${input}`,
                  };

                case "to":
                  return {
                    tailwind: `to-[${input}]`,
                    css: `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`,
                    //css: `--tw-gradient-to-position: ${input}`,
                  };

                case "via":
                  return {
                    tailwind: `via-[${input}]`,
                    //`--tw-gradient-stops: ${input}`
                    css: `--tw-gradient-stops: var(--tw-gradient-from), ${input} var(--tw-gradient-via-position), var(--tw-gradient-to);`,
                  };
              }
            }}
            onClear={() => {
              if (gradient?.from?.color?.classname) {
                removeTailwindClass(gradient.from.color.classname);
              }
            }}
          />
        </>
      )}
      {view === ViewType.Image && (
        <>
          <CustomImageUpload
            value={backgroundImage}
            evaluatedValue={backgroundImage}
            setValue={setBackgroundImage}
            set={backgroundImageSet}
            setSet={setBackgroundImageSet}
            buildOptionPreview={buildBackgroundColorPreview}
            buildArbitraryClass={(input: string) => {
              const url = `url('${input}')`;
              return {
                tailwind: `bg-[${url}]`,
                css: `background-image: ${url}`,
              };
            }}
            onComboboxSelected={(option) => addTailwindClass(option)}
            onClear={() => {
              const imageClass = classList.find((c) =>
                c.startsWith("bg-[url(")
              );
              const gradientClass = classList.find((c) =>
                c.startsWith("bg-gradient-")
              );

              const toRemove = imageClass || gradientClass;

              if (toRemove) {
                removeTailwindClass(toRemove);
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default React.memo(FillModal);
